import { useCallback, useEffect, useRef, Dispatch } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChatSession,
  InvestigationSummary,
  Customer,
} from '../../../types';
import { createInvestigationService } from '../../investigation/services/investigationService';
import { useAuth } from '../../auth/context/AuthContext';
import { ChatAction } from './useChatReducer';
import { chatMessageKeys } from './chatMessageMapping';

const POLL_INTERVAL_MS = 5000;
// A read can fail for a reason that will not clear (the investigation was
// deleted, or this operator lost access to it). Retrying those forever leaves
// the reader watching a spinner that can never resolve, so the chain gives up
// after this many consecutive failures and says so.
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

const getTabInvestigationKey = (customerId: string) => `strands.tabInvestigationId.${customerId}`;
const getLastInvestigationKey = (customerId: string) => `strands.lastInvestigationId.${customerId}`;

interface UseChatSessionsParams {
  dispatch: Dispatch<ChatAction>;
  customerId?: string;
  initialInvestigationId?: string | null;
}

export function useChatSessions({
  dispatch,
  customerId,
  initialInvestigationId,
}: UseChatSessionsParams) {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();
  const initialInvestigationIdRef = useRef(initialInvestigationId);
  initialInvestigationIdRef.current = initialInvestigationId;
  const lastLoadAttemptRef = useRef<string | null>(null);
  const pollingAbortRef = useRef(false);
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const prevCustomerIdRef = useRef<string | undefined>(undefined);
  const selectGenerationRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      pollingAbortRef.current = true;
      if (pollingTimerRef.current) clearTimeout(pollingTimerRef.current);
    };
  }, []);

  const selectSession = useCallback(async (sessionId: string) => {
    if (!idToken || !customerId) return;

    const generation = ++selectGenerationRef.current;

    sessionStorage.setItem(getTabInvestigationKey(customerId), sessionId);
    localStorage.setItem(getLastInvestigationKey(customerId), sessionId);

    try {
      const investigationService = createInvestigationService(idToken);

      const investigation = await investigationService.getInvestigationById(customerId, sessionId);

      // A newer selection (another investigation, or a customer switch) started
      // while this one was in flight — drop the stale response.
      if (generation !== selectGenerationRef.current) return;

      // The message list is loaded by useConversationMessages off the query cache;
      // the session carries investigation metadata only.
      dispatch({ type: 'SESSION_SELECTED', session: investigation });

      if (investigation.agent_status === 'thinking') {
        dispatch({
          type: 'THINKING',
          message: 'Agent is working on your request...'
        });

        pollingAbortRef.current = false;
        if (pollingTimerRef.current) clearTimeout(pollingTimerRef.current);

        let consecutiveFailures = 0;
        const pollForCompletion = async () => {
          if (pollingAbortRef.current || !isMountedRef.current || generation !== selectGenerationRef.current) return;
          try {
            const updated = await investigationService.getInvestigationById(customerId, sessionId);
            if (pollingAbortRef.current || !isMountedRef.current || generation !== selectGenerationRef.current) return;

            if (updated.agent_status === 'completed') {
              // The agent finished while away — refetch the persisted history into
              // the cache rather than re-dispatching messages onto the session.
              queryClient.invalidateQueries({ queryKey: chatMessageKeys.list(customerId, sessionId) });

              dispatch({
                type: 'SESSION_SELECTED',
                session: updated
              });
              dispatch({
                type: 'THINKING',
                message: 'Agent completed work while you were away'
              });
            } else if (updated.agent_status === 'thinking') {
              if (isMountedRef.current) {
                pollingTimerRef.current = setTimeout(pollForCompletion, POLL_INTERVAL_MS);
              }
            } else if (updated.agent_status === 'error') {
              dispatch({
                type: 'ERROR',
                error: 'Agent encountered an error'
              });
            } else {
              // `idle` lands here, and it is not a leftover case: a turn whose
              // client vanished mid-stream is released as IDLE, because the
              // COMPLETED and ERROR upgrades both sit after the stream's final
              // yield. With no branch this fell through without rescheduling and
              // the chain died on the spot, leaving a question with no answer and
              // no spinner to explain it. The turn is over either way, so refetch
              // the persisted history and stop polling.
              //
              // Deliberately not CLEAR_STREAMING: that reducer empties
              // messageQueue, and a follow-up the operator typed while waiting is
              // theirs, not this poll's to discard (#1418).
              queryClient.invalidateQueries({ queryKey: chatMessageKeys.list(customerId, sessionId) });
              dispatch({ type: 'SESSION_SELECTED', session: updated });
            }
            consecutiveFailures = 0;
          } catch (pollError) {
            if (pollingAbortRef.current || !isMountedRef.current || generation !== selectGenerationRef.current) return;
            // One failed read says nothing about whether the turn is running, and
            // giving up on it is indistinguishable to the reader from an agent
            // that never answered. A run of them says something else: the read is
            // not coming back, and pretending otherwise is the same spinner by a
            // different route.
            consecutiveFailures += 1;
            console.error(
              `Failed to poll investigation status (${consecutiveFailures}/${MAX_CONSECUTIVE_POLL_FAILURES}):`,
              pollError,
            );
            if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
              dispatch({
                type: 'ERROR',
                error: 'Lost contact with the investigation while waiting for the agent.',
              });
              return;
            }
            if (isMountedRef.current && !pollingAbortRef.current
                && generation === selectGenerationRef.current) {
              pollingTimerRef.current = setTimeout(pollForCompletion, POLL_INTERVAL_MS);
            }
          }
        };

        pollForCompletion();
      } else if (investigation.agent_status === 'completed') {
        const lastActivity = investigation.last_agent_activity;
        if (lastActivity) {
          const activityTime = new Date(lastActivity).getTime();
          const now = Date.now();
          const thirtySecondsAgo = now - 30000;

          if (activityTime > thirtySecondsAgo) {
            dispatch({
              type: 'THINKING',
              message: 'Agent completed work recently'
            });
          }
        }
      }
      return investigation;
    } catch (error) {
      console.error('Failed to load investigation:', error);
      dispatch({ type: 'ERROR', error: 'Failed to load investigation' });
      // Genuinely could not be loaded (e.g. deleted): signal not-found so callers
      // can self-heal a stale deep link. Superseded/no-auth returns undefined above.
      return null;
    }
  }, [customerId, idToken, dispatch, queryClient]);

  useEffect(() => {
    if (customerId && idToken) {
      // Switching customer: drop the previous customer's sessions and current
      // session immediately, so the sidebar never shows another customer's
      // investigations while the new list loads. Guard on customerId change only
      // — a bare token refresh (same customer) must not clear the list.
      if (prevCustomerIdRef.current !== undefined && prevCustomerIdRef.current !== customerId) {
        // Invalidate any selectSession still in flight for the previous customer.
        // Its own generation guard is only bumped when selectSession runs again;
        // if the new customer has no investigation to restore it never does, so
        // the stale response would otherwise repopulate currentSession here.
        selectGenerationRef.current++;
        dispatch({ type: 'SESSIONS_LOADED', sessions: [] });
        dispatch({ type: 'SESSION_CLEARED' });
      }
      prevCustomerIdRef.current = customerId;

      const loadKey = `${customerId}:${idToken.slice(-20)}`;
      if (lastLoadAttemptRef.current === loadKey) {
        console.log('Skipping duplicate load attempt to prevent retry loop');
        return;
      }

      let cancelled = false;
      const loadData = async () => {
        try {
          const investigationService = createInvestigationService(idToken);

          const investigations = await investigationService.getInvestigationsForCustomer(customerId);
          if (cancelled) return;

          lastLoadAttemptRef.current = null;

          dispatch({ type: 'SESSIONS_LOADED', sessions: investigations });

          const tabInvestigationId = sessionStorage.getItem(getTabInvestigationKey(customerId));
          const lastInvestigationId = localStorage.getItem(getLastInvestigationKey(customerId));
          const investigationToRestore = initialInvestigationIdRef.current || tabInvestigationId || lastInvestigationId;

          if (investigationToRestore && investigations.some(inv => inv.id === investigationToRestore)) {
            await selectSession(investigationToRestore);
          }
        } catch {
          if (cancelled) return;
          lastLoadAttemptRef.current = loadKey;
          dispatch({ type: 'ERROR', error: 'Failed to load investigations' });
        }
      };
      loadData();
      return () => { cancelled = true; };
    } else {
      prevCustomerIdRef.current = customerId;
      lastLoadAttemptRef.current = null;
      dispatch({ type: 'SESSIONS_LOADED', sessions: [] });
      dispatch({ type: 'SESSION_CLEARED' });
    }
  }, [customerId, idToken, selectSession, dispatch]);

  const createNewSession = useCallback(async (customer?: Customer) => {
    if (!customer || !idToken) return null;

    try {
      const investigationService = createInvestigationService(idToken);
      // No title: the aggregate defaults to 'Untitled Investigation', which is the exact string
      // investigations:UpdateInvestigationMetadata is told to treat as "not named yet". Sending
      // any other wording here is what kept the agent from ever naming an investigation from its
      // first message.
      const newSession = await investigationService.createInvestigation(customer.id, {});

      // A load of another investigation still in flight must not land on top.
      selectGenerationRef.current++;
      dispatch({ type: 'SESSION_CREATED', session: newSession });
      return newSession;
    } catch {
      dispatch({ type: 'ERROR', error: 'Failed to create investigation' });
      return null;
    }
  }, [idToken, dispatch]);

  const deleteSession = useCallback(async (sessionId: string) => {
    if (!idToken || !customerId) return;

    try {
      const investigationService = createInvestigationService(idToken);
      await investigationService.deleteInvestigation(customerId, sessionId);

      dispatch({ type: 'SESSION_DELETED', sessionId });
    } catch {
      dispatch({ type: 'ERROR', error: 'Failed to delete investigation' });
    }
  }, [customerId, idToken, dispatch]);

  const clearCurrentSession = useCallback(() => {
    dispatch({ type: 'SESSION_CLEARED' });
  }, [dispatch]);

  const refreshInvestigation = useCallback(async (investigationId: string) => {
    if (!idToken || !customerId) return;

    try {
      const investigationService = createInvestigationService(idToken);
      const updatedInvestigation = await investigationService.getInvestigationById(customerId, investigationId);

      // SESSION_UPDATED carries investigation metadata only; the message list lives
      // in the query cache.
      dispatch({
        type: 'SESSION_UPDATED',
        session: updatedInvestigation
      });
    } catch {
      dispatch({ type: 'ERROR', error: 'Failed to refresh investigation' });
    }
  }, [customerId, idToken, dispatch]);

  const setCurrentSession = useCallback((session: ChatSession | null) => {
    // A direct pick supersedes any selectSession still loading another investigation.
    selectGenerationRef.current++;
    if (session) {
      dispatch({ type: 'SESSION_SELECTED', session });
    } else {
      dispatch({ type: 'SESSION_CLEARED' });
    }
  }, [dispatch]);

  const setSessions = useCallback((sessions: InvestigationSummary[]) => {
    dispatch({ type: 'SESSIONS_LOADED', sessions });
  }, [dispatch]);

  return {
    selectSession,
    createNewSession,
    deleteSession,
    clearCurrentSession,
    refreshInvestigation,
    setCurrentSession,
    setSessions,
  };
}
